import { useCallback, useEffect, useId, useRef } from 'react';
import styles from './Modal.module.css';

// Tab-cycle boundary set — the controls these 27 modals actually render
// (Button emits <button>, fields are input/select/textarea, plus the close X).
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

// `getClientRects()`, not `offsetParent`: the overlay is position:fixed and a
// fixed element reports a null offsetParent even while painted, which would
// empty this list and silently disable the cycle.
const findFocusable = (root) =>
  Array.from(root?.querySelectorAll(FOCUSABLE_SELECTOR) ?? [])
    .filter((element) => element.getClientRects().length > 0);

const Modal = ({ isOpen, onClose, title, children, size = 'medium', manageFocus = false }) => {
  const titleId = useId();
  const modalRef = useRef(null);
  const restoreRef = useRef(null);

  const handleEscape = useCallback((e) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleEscape]);

  // Focus in on open, restore to the opener on close. Opt-in (D-A11Y-2, 2026-09-19):
  // the other 26 consumers keep today's behaviour, and AdminFiltersModal already
  // focuses its own first field — making this unconditional would double-focus it.
  useEffect(() => {
    if (!manageFocus || !isOpen) return undefined;
    restoreRef.current = document.activeElement;
    const [firstFocusable] = findFocusable(modalRef.current);
    (firstFocusable ?? modalRef.current)?.focus();
    return () => restoreRef.current?.focus?.();
  }, [manageFocus, isOpen]);

  if (!isOpen) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Tab cycles within the dialog, so it can never reach the page behind the
  // overlay. Escape is deliberately NOT handled here: the combobox in
  // OpenTicketModal swallows the first Escape to close its listbox and lets the
  // second reach the document listener above.
  const handleKeyDown = (e) => {
    if (!manageFocus || e.key !== 'Tab') return;
    const focusable = findFocusable(modalRef.current);
    if (focusable.length === 0) return;
    const [first] = focusable;
    const last = focusable[focusable.length - 1];
    const [edge, wrapTo] = e.shiftKey ? [first, last] : [last, first];
    if (document.activeElement !== edge) return;
    e.preventDefault();
    wrapTo.focus();
  };

  return (
    <div className={styles.overlay} onClick={handleBackdropClick}>
      <div
        ref={modalRef}
        className={`${styles.modal} ${styles[size]}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
      >
        <div className={styles.header}>
          <h2 className={styles.title} id={titleId}>{title}</h2>
          <button className={styles.closeBtn} onClick={onClose} aria-label="Close">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className={styles.body}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
